// Vendor imports
const _ = require('lodash')
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { imageLayers } = require('shared/css-utils')
// Client V2 common imports
const { composeRefs } = require('client-v2/common/components/utils')
const tag = require('client-v2/common/utils/hyperscript')
const ViewportObserver = require('client-v2/common/utils/viewport-observer')

const bem = BemEntity('LazyImage')

let viewportObserver
if (typeof window === 'object') {
  viewportObserver = new ViewportObserver({
    callback: (domElement, reactElement) => { reactElement.setVisible() },
    intersectionObserverOptions: {
      root: null,
      rootMargin: '200px',
      threshold: 0
    }
  })
}

class LazyImage extends React.PureComponent {
  constructor(props) {
    super(props)

    this.ref = React.createRef()
    this.imageRef = React.createRef()
    this.state = {
      cached: false,
      failed: false,
      loaded: false,
      sourceIndex: 0,
      sources: normalizeSources(props.sources),
      visible: props.visible
    }
    this.onImageError = this.onImageError.bind(this)
    this.onImageLoad = this.onImageLoad.bind(this)
  }

  componentDidMount() {
    this.observeVisibility()
    const imageEl = this.imageRef.current
    if (this.state.visible && imageEl) {
      const cached = (
        imageEl.complete ||
        imageEl.readyState === 4 ||
        imageEl.readyState === 'complete'
      )
      if (cached) {
        this.setState(() => ({ cached: true, loaded: true }))
      }
    }
  }

  componentWillUnmount() {
    this.unobserveVisibility()
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.visible !== this.props.visible) {
      this.setState(
        (state, props) => ({ visible: props.visible }),
        () => this.observeVisibility()
      )
    }
    if (!_.isEqual(prevProps.sources, this.props.sources)) {
      this.setState((state, props) => {
        // We don't get the image load event if it doesn't change
        const prevLoadedSource = state.sources[state.sourceIndex]
        const sources = normalizeSources(props.sources)
        const sameSource = prevLoadedSource === sources[0]
        return {
          failed: sources.length === 0,
          loaded: sameSource && state.visible,
          sourceIndex: 0,
          sources
        }
      })
    }
    if (!prevState.failed && this.state.failed && this.props.onError) {
      this.props.onError(this.state.sources)
    }
    if (!prevState.loaded && this.state.loaded && this.props.onLoad) {
      this.props.onLoad(this.src())
    }
  }

  observeVisibility() {
    const el = this.ref.current
    if (!this.state.visible && viewportObserver && el) {
      viewportObserver.observe(el, this)
    }
  }

  unobserveVisibility() {
    const el = this.ref.current
    if (viewportObserver && el) {
      viewportObserver.unobserve(el)
    }
  }

  setVisible() {
    this.setState(() => ({ visible: true }))
  }

  // Event handler methods

  onImageError() {
    this.setState(({ sources, sourceIndex }) => ({
      failed: sourceIndex >= sources.length,
      sourceIndex: sourceIndex + 1
    }))
  }

  onImageLoad() {
    this.setState(() => ({ loaded: true }))
  }

  // Prop methods

  loading() {
    return this.state.visible && !!this.src() && !this.state.loaded
  }

  src() {
    if (this.state.visible) {
      return this.state.sources[this.state.sourceIndex]
    }
  }

  style() {
    /**
     * Override `background-image` provided by `placeholder` prop or CSS.
     * This prevents images with alpha channel from looking bad.
     * @see https://app.asana.com/0/102902769366319/1128570656009822/f
     */
    if (this.state.loaded && this.props.hidePlaceholderAfterLoaded) {
      return { backgroundImage: 'none' }
    }
    const backgroundImage = imageLayers(this.props.placeholder)
    if (backgroundImage) {
      return { backgroundImage }
    }
  }

  // Render methods

  renderImage() {
    const src = this.src()
    if (!src) {
      return null
    }
    return (
      tag('img', {
        ref: this.imageRef,
        alt: this.props.alt,
        className: bem.element('image', {
          cached: this.state.cached,
          failed: this.state.failed,
          loaded: this.state.loaded,
          loading: this.loading()
        }),
        src,
        onError: this.onImageError,
        onLoad: this.onImageLoad
      })
    )
  }

  render() {
    const props = _.omit(this.props, LazyImage.reservedPropKeys)
    const tagName = props.href || props.onClick ? 'a' : 'div'
    const ref = composeRefs(this.ref, this.props.forwardedRef)
    return (
      tag(tagName, {
        ...props,
        ref,
        className: classNames(
          props.className,
          bem.block({
            failed: this.state.failed,
            loaded: this.state.loaded,
            loading: this.loading()
          })
        ),
        style: {
          ...props.style,
          ...this.style()
        }
      },
        this.renderImage()
      )
    )
  }
}

// These prop keys will not get passed into DOM element
LazyImage.reservedPropKeys = [
  'alt',
  'children',
  'forwardedRef',
  'hidePlaceholderAfterLoaded',
  'placeholder',
  'sources',
  'visible',
  'onError',
  'onLoad'
]

LazyImage.defaultProps = {
  alt: '',
  hidePlaceholderAfterLoaded: true,
  visible: false
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')
  const morePropTypes = require('client-v2/common/utils/prop-types')

  LazyImage.propTypes = {
    // NOTE: Passes down all other props to its DOM node
    alt: PropTypes.string,
    className: PropTypes.string,
    forwardedRef: morePropTypes.domRef,
    hidePlaceholderAfterLoaded: PropTypes.bool,
    placeholder: PropTypes.string,
    sources: PropTypes.arrayOf(PropTypes.string).isRequired,
    visible: PropTypes.bool,
    // Callbacks
    onError: PropTypes.func,
    onLoad: PropTypes.func
  }
}

module.exports = LazyImage

function normalizeSources(sources) {
  return _.chain(sources)
  .uniq()
  .filter()
  .value()
}
